import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, ArrowRight } from "lucide-react";

export default function JoinDatasets() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [ds, setDs] = useState([]);
  const [left, setLeft] = useState(null); const [right, setRight] = useState(null);
  const [leftDs, setLeftDs] = useState(null); const [rightDs, setRightDs] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", left_key: "", right_key: "", how: "inner" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/datasets").then(({ data }) => setDs(data.datasets || [])); }, []);
  useEffect(() => {
    if (left) api.get(`/datasets/${left}`).then(({ data }) => {
      setLeftDs(data);
      const cand = ["employee_id","id","emp_id","user_id"];
      setForm(f => ({ ...f, left_key: data.columns.find(c => cand.includes(c.toLowerCase())) || data.columns[0] }));
    }); else setLeftDs(null);
  }, [left]);
  useEffect(() => {
    if (right) api.get(`/datasets/${right}`).then(({ data }) => {
      setRightDs(data);
      const cand = ["employee_id","id","emp_id","user_id"];
      setForm(f => ({ ...f, right_key: data.columns.find(c => cand.includes(c.toLowerCase())) || data.columns[0] }));
    }); else setRightDs(null);
  }, [right]);

  const submit = async (e) => {
    e.preventDefault();
    if (!left || !right) return toast.error("Pick two datasets");
    if (left === right) return toast.error("Pick two different datasets");
    if (!form.name.trim()) return toast.error("Name the joined dataset");
    setBusy(true);
    try {
      const { data } = await api.post("/datasets/join", {
        name: form.name, description: form.description,
        left_id: left, right_id: right, left_key: form.left_key, right_key: form.right_key, how: form.how,
      });
      toast.success(`Joined — ${data.row_count} rows`);
      nav(`/t/${slug}/datasets/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Join failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10">
      <div className="label-caps mb-2">Admin · Combine</div>
      <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Join Datasets</h1>
      <p className="text-slate-500 mt-3 mb-8">Combine two uploaded files by a shared key (like <span className="mono">employee_id</span>) so a dashboard can pull from both systems together.</p>

      {ds.length < 2 ? <div className="card text-sm text-slate-500">Upload at least 2 datasets to use joins.</div> : (
        <form onSubmit={submit} className="space-y-6" data-testid="join-form">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            <Picker label="Left dataset" value={left} onChange={setLeft} datasets={ds} exclude={right} preview={leftDs} testId="left" />
            <div className="flex items-center justify-center h-10 md:h-32">
              <div className="w-10 h-10 rounded-full border border-[var(--border)] bg-white flex items-center justify-center">
                <GitMerge size={16} className="text-[var(--primary)]" />
              </div>
            </div>
            <Picker label="Right dataset" value={right} onChange={setRight} datasets={ds} exclude={left} preview={rightDs} testId="right" />
          </div>

          {leftDs && rightDs && (
            <div className="card space-y-4">
              <div className="label-caps">Match on</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                <select data-testid="left-key" value={form.left_key} onChange={(e) => setForm({...form, left_key: e.target.value})} className="input-base mono">
                  {leftDs.columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <ArrowRight size={16} className="text-slate-400 mx-auto" />
                <select data-testid="right-key" value={form.right_key} onChange={(e) => setForm({...form, right_key: e.target.value})} className="input-base mono">
                  {rightDs.columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div>
                  <label className="label-caps block mb-2">Join type</label>
                  <select data-testid="join-how" value={form.how} onChange={(e) => setForm({...form, how: e.target.value})} className="input-base">
                    <option value="inner">Inner — rows in both</option>
                    <option value="left">Left — all left rows</option>
                    <option value="outer">Full — all rows</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Name</label>
                  <input data-testid="join-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" placeholder="e.g. Employees + Compensation" />
                </div>
              </div>
              <div>
                <label className="label-caps block mb-2">Description</label>
                <textarea data-testid="join-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-base" rows={2} />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={busy || !left || !right} data-testid="join-submit" className="btn-primary">
              <GitMerge size={14} /> {busy ? "Joining…" : "Create joined dataset"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Picker({ label, value, onChange, datasets, exclude, preview, testId }) {
  return (
    <div>
      <label className="label-caps block mb-2">{label}</label>
      <select data-testid={`${testId}-select`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className="input-base">
        <option value="">Select…</option>
        {datasets.filter(d => d.id !== exclude).map(d => <option key={d.id} value={d.id}>{d.name} ({d.row_count} rows)</option>)}
      </select>
      {preview && (
        <div className="mt-3 card p-3">
          <div className="label-caps mb-2">{preview.row_count} rows · {preview.columns.length} cols</div>
          <div className="flex flex-wrap gap-1">
            {preview.columns.slice(0, 8).map(c => <span key={c} className="text-[10px] mono chip">{c}</span>)}
            {preview.columns.length > 8 && <span className="text-[10px] text-slate-500">+{preview.columns.length - 8} more</span>}
          </div>
        </div>
      )}
    </div>
  );
}
